import { getColumnTemplateById, getColumnTemplatesQuery, getDefaultColumnTemplate } from './http/columnTemplate';
import type { QueryClient, QueryObserverOptions } from '@tanstack/react-query';
import type {
    ColumnTemplate,
    ColumnTemplateRecord,
    ConcreteColumnTemplate,
    KnownColumnTemplateKey,
} from '@thinkalpha/ui-types/contracts/column-template';
import type { ResourceQuery } from '@thinkalpha/ui-types/contracts/resource-query';
import { firstValueFrom } from 'rxjs';
import { getColumnTemplateRecords } from 'src/lib/column-templates';
import type { LibraryQueryResult } from 'src/models/LibraryModel';

// Constants
export const COLUMN_TEMPLATES = 'column-templates';
export const LIBRARY = 'library';

/**
 * Generates a query to get a ConcreteColumnTemplate by id
 *
 * @param id a string ConcreteColumnTemplate id, or null
 * @returns an object with queryKey and queryFn
 */
export function getColumnTemplateByIdQuery(id: string): QueryObserverOptions<ConcreteColumnTemplate> {
    return {
        queryKey: [COLUMN_TEMPLATES, id],
        queryFn: () => getColumnTemplateById(id),
    };
}

export function hydrateColumnTemplateByIdQuery(queryClient: QueryClient, columnTemplate: ConcreteColumnTemplate): void {
    queryClient.setQueryData(getColumnTemplateByIdQuery(columnTemplate.id).queryKey, columnTemplate);
}

/**
 * Generates a query to get a default ConcreteColumnTemplate by key
 *
 * @param id a KnownColumnTemplateKey
 * @returns an object with queryKey and queryFn
 */
export function getDefaultColumnTemplateQuery(
    key: KnownColumnTemplateKey,
): QueryObserverOptions<ConcreteColumnTemplate> {
    return {
        queryKey: [COLUMN_TEMPLATES, 'default', key],
        queryFn: () => getDefaultColumnTemplate(key),
    };
}

// Record lists are keyed by template key, which saved templates carry as a plain string.
const columnTemplateRecordsQueryKey = (key: string) => ['column-template-records', key];

export function getColumnTemplateRecordsQuery(
    key: KnownColumnTemplateKey,
): QueryObserverOptions<ColumnTemplateRecord[]> {
    return {
        queryKey: columnTemplateRecordsQueryKey(key),
        queryFn: () => firstValueFrom(getColumnTemplateRecords(key)),
    };
}

/**
 * Merges a saved template into the cached record list for its own key. The template library is not
 * filtered by key, so that can differ from the key of the menu the template was saved from. A list
 * that was never loaded stays unloaded: caching an empty list would read as fresh and hide every
 * template in that key's menu until it goes stale.
 */
export function hydrateSavedColumnTemplateRecord(
    queryClient: QueryClient,
    columnTemplate: ConcreteColumnTemplate,
    fallbackKey?: KnownColumnTemplateKey,
): void {
    const key = columnTemplate.key ?? fallbackKey;
    if (key === undefined) {
        return;
    }
    queryClient.setQueryData<ColumnTemplateRecord[]>(columnTemplateRecordsQueryKey(key), (records) =>
        records?.map((record) =>
            record.id === columnTemplate.id ? ({ ...record, ...columnTemplate } as ColumnTemplateRecord) : record,
        ),
    );
}

export function getLiteralColumnTemplateQuery(columnTemplate: ColumnTemplate): QueryObserverOptions<ColumnTemplate> {
    return {
        queryKey: [COLUMN_TEMPLATES, 'literal', columnTemplate],
        queryFn: () => columnTemplate,
        gcTime: 0,
    };
}

export function getColumnTemplateRecordsForLibraryQuery(
    resourceQuery: ResourceQuery,
    enabled = true,
): QueryObserverOptions<LibraryQueryResult<ConcreteColumnTemplate>> {
    return {
        queryKey: [COLUMN_TEMPLATES, LIBRARY, resourceQuery],
        queryFn: async () => {
            const result = await getColumnTemplatesQuery(resourceQuery);

            return {
                total: result.count,
                rows: result.results,
            };
        },
        enabled,
    };
}
